/**
 * What the vault keeps secret, and how user input becomes it.
 *
 *   mnemonic     a BIP39 seed phrase (12 or 24 words, checksum checked). Account 0 of each chain
 *                family, on the same paths MetaMask / TronLink / Electrum-Sparrow / Phantom use.
 *   privateKeys  one imported private key per chain family (EVM, Tron, Bitcoin, Solana).
 *
 * Private keys are kept as 64 hex characters whatever format they were typed in.
 * Nothing here talks to the network; addresses are worked out locally.
 */
import { addressFromPrivateKey, deriveKey, derivationPath, isValidMnemonic, mnemonicToSeed, normalizeMnemonic, KEY_FAMILIES, } from "@custos/chain-adapters/keys";
import { sha256 } from "@noble/hashes/sha2.js";
import { bytesToHex, hexToBytes } from "@noble/hashes/utils.js";
import { base58, createBase58check } from "@scure/base";
export { KEY_FAMILIES };
/** A user typing mistake — the message is safe to show (it never contains the input). */
export class VaultInputError extends Error {
    constructor(message) {
        super(message);
        this.name = "VaultInputError";
    }
}
const base58check = createBase58check(sha256);
// ───────────────────────── Seed phrases ─────────────────────────
/** Cleans up spacing/case and checks the word count and BIP39 checksum. */
export function parseMnemonic(input) {
    const words = normalizeMnemonic(input).split(" ").filter(Boolean);
    if (words.length !== 12 && words.length !== 24) {
        throw new VaultInputError(`A recovery phrase has 12 or 24 words — this one has ${words.length}.`);
    }
    const phrase = words.join(" ");
    if (!isValidMnemonic(phrase)) {
        throw new VaultInputError("This recovery phrase isn't valid. Check each word and their order.");
    }
    return phrase;
}
// ───────────────────────── Private keys ─────────────────────────
const HEX64 = /^(0x)?[0-9a-fA-F]{64}$/;
function parseWif(input, mode) {
    // Electrum writes "p2wpkh:<WIF>"; only native SegWit keys are supported.
    const wif = input.replace(/^p2wpkh:/, "");
    if (/^p2(pkh|sh|wpkh-p2sh):/.test(input)) {
        throw new VaultInputError("Only native SegWit (bc1… / tb1…) Bitcoin keys are supported.");
    }
    let bytes;
    try {
        bytes = base58check.decode(wif);
    }
    catch {
        throw new VaultInputError("This doesn't look like a Bitcoin private key (WIF). Check it and try again.");
    }
    const version = mode === "mainnet" ? 0x80 : 0xef;
    if (bytes[0] !== 0x80 && bytes[0] !== 0xef) {
        throw new VaultInputError("This doesn't look like a Bitcoin private key (WIF). Check it and try again.");
    }
    if (bytes[0] !== version) {
        throw new VaultInputError(mode === "mainnet"
            ? "This is a TESTNET Bitcoin key. Use a mainnet key (it starts with K or L)."
            : "This is a MAINNET Bitcoin key. On testnet use a testnet key (it starts with c).");
    }
    if (bytes.length === 33) {
        throw new VaultInputError("Uncompressed Bitcoin keys are not supported (they can't have bc1 addresses).");
    }
    if (bytes.length !== 34 || bytes[33] !== 0x01) {
        throw new VaultInputError("This doesn't look like a Bitcoin private key (WIF). Check it and try again.");
    }
    return bytes.slice(1, 33);
}
function parseSolanaKey(input) {
    let bytes;
    try {
        if (input.startsWith("[")) {
            // solana-keygen JSON file: [12, 34, …] (64 numbers)
            const numbers = JSON.parse(input);
            if (!Array.isArray(numbers) || !numbers.every((n) => Number.isInteger(n) && n >= 0 && n <= 255)) {
                throw new Error("bad array");
            }
            bytes = Uint8Array.from(numbers);
        }
        else if (HEX64.test(input)) {
            bytes = hexToBytes(input.replace(/^0x/, ""));
        }
        else {
            bytes = base58.decode(input);
        }
    }
    catch {
        throw new VaultInputError("This doesn't look like a Solana private key. Check it and try again.");
    }
    if (bytes.length === 64) {
        // Phantom / Solflare export: 32-byte seed ‖ 32-byte public key. Check they belong together.
        const seed = bytes.slice(0, 32);
        const expected = base58.encode(bytes.slice(32));
        if (addressFromPrivateKey("SOLANA", seed, "testnet") !== expected) {
            throw new VaultInputError("This Solana key is damaged (its two halves don't match).");
        }
        return seed;
    }
    if (bytes.length === 32)
        return bytes;
    throw new VaultInputError("This doesn't look like a Solana private key. Check it and try again.");
}
/**
 * Accepted formats:
 *   EVM / Tron   64 hex characters, with or without 0x (MetaMask, TronLink exports)
 *   Bitcoin      WIF, compressed (K…/L… mainnet, c… testnet; Electrum "p2wpkh:" prefix ok)
 *   Solana       base58 (Phantom/Solflare, 64-byte), 32-byte base58/hex, or a solana-keygen [..] array
 * Returns the 32 raw key bytes (caller wipes them).
 */
export function parsePrivateKey(family, input, mode) {
    const text = input.trim();
    let key;
    switch (family) {
        case "EVM":
        case "TRON":
            if (!HEX64.test(text)) {
                throw new VaultInputError("A private key here is 64 characters (0-9, a-f), optionally starting with 0x.");
            }
            key = hexToBytes(text.replace(/^0x/, "").toLowerCase());
            break;
        case "BITCOIN":
            key = parseWif(text, mode);
            break;
        case "SOLANA":
            key = parseSolanaKey(text);
            break;
    }
    try {
        addressFromPrivateKey(family, key, mode);
    }
    catch {
        key.fill(0);
        throw new VaultInputError("This private key isn't valid.");
    }
    return key;
}
// ───────────────────────── Accounts ─────────────────────────
/** Public addresses of everything in the vault. Private keys are wiped before returning. */
export function vaultAccounts(secret, mode) {
    if (secret.kind === "mnemonic") {
        const seed = mnemonicToSeed(secret.mnemonic);
        try {
            return KEY_FAMILIES.map((family) => {
                const key = deriveKey(seed, family, 0, mode);
                key.privateKey.fill(0);
                return { family, address: key.address, path: derivationPath(family, 0, mode) };
            });
        }
        finally {
            seed.fill(0);
        }
    }
    return KEY_FAMILIES.flatMap((family) => {
        const hex = secret.keys[family];
        if (!hex)
            return [];
        const key = hexToBytes(hex);
        try {
            return [{ family, address: addressFromPrivateKey(family, key, mode), path: null }];
        }
        finally {
            key.fill(0);
        }
    });
}
/**
 * Lends the private key of one family to `use` and wipes it afterwards (Phase 9b: signing).
 * Throws if the vault has no key for that family.
 */
export function withPrivateKey(secret, family, mode, use) {
    let key;
    if (secret.kind === "mnemonic") {
        const seed = mnemonicToSeed(secret.mnemonic);
        try {
            key = deriveKey(seed, family, 0, mode).privateKey;
        }
        finally {
            seed.fill(0);
        }
    }
    else {
        const hex = secret.keys[family];
        if (!hex)
            throw new Error(`The vault has no ${family} key`);
        key = hexToBytes(hex);
    }
    let result;
    try {
        result = use(key);
    }
    catch (error) {
        key.fill(0);
        throw error;
    }
    // Async signers (EVM): wipe once the promise settles, not before.
    if (result instanceof Promise)
        return result.finally(() => key.fill(0));
    key.fill(0);
    return result;
}
export function privateKeyToHex(key) {
    return bytesToHex(key);
}
/**
 * A stored private key in the format other wallets import (for the backup screen):
 * EVM / Tron 64 hex (MetaMask, TronLink), Bitcoin compressed WIF (Electrum/Sparrow: choose
 * native SegWit), Solana base58 of seed ‖ public key (Phantom, Solflare).
 */
export function exportPrivateKey(family, hex, mode) {
    const key = hexToBytes(hex);
    try {
        switch (family) {
            case "EVM":
            case "TRON":
                return bytesToHex(key);
            case "BITCOIN":
                return base58check.encode(Uint8Array.from([mode === "mainnet" ? 0x80 : 0xef, ...key, 0x01]));
            case "SOLANA": {
                const pub = base58.decode(addressFromPrivateKey("SOLANA", key, mode));
                return base58.encode(Uint8Array.from([...key, ...pub]));
            }
        }
    }
    finally {
        key.fill(0);
    }
}
//# sourceMappingURL=secret.js.map